import { Check, Plus, RotateCcw } from "lucide-react";
import {
  CanAccess,
  ListBase,
  useCanAccess,
  useListContext,
  useRedirect,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";

import { MobileContent } from "../layout/MobileContent";
import { MobileListHeader } from "../layout/MobileListHeader";
import { TopToolbar } from "../layout/TopToolbar";
import type { ContractType } from "../types";

const ContractTypeListActions = () => (
  <TopToolbar>
    <CanAccess resource="contract_types" action="create">
      <CreateButton label="resources.contract_types.action.new" />
    </CanAccess>
  </TopToolbar>
);

const filters = [<SearchInput source="q" alwaysOn />];

/**
 * Contract types list. Everybody may read it (the sponsoring form needs it);
 * only administrators may create or edit one.
 */
export function ContractTypeList() {
  const translate = useTranslate();
  const { canAccess: canEdit } = useCanAccess({
    resource: "contract_types",
    action: "edit",
  });

  return (
    <List
      filters={filters}
      actions={<ContractTypeListActions />}
      sort={{ field: "sort_order", order: "ASC" }}
      perPage={25}
    >
      <DataTable<ContractType>
        rowClick={canEdit ? "edit" : false}
        bulkActionButtons={false}
        className="bg-card"
      >
        <DataTable.Col<ContractType> source="name" />
        <DataTable.Col<ContractType>
          source="active"
          render={(record) =>
            record.active ? (
              <Check
                className="size-4"
                aria-label={translate("resources.contract_types.fields.active")}
              />
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                {translate("resources.contract_types.inactive")}
              </Badge>
            )
          }
        />
        <DataTable.Col<ContractType>
          source="sort_order"
          className="text-right"
          headerClassName="text-right"
        />
      </DataTable>
      <p className="text-xs text-muted-foreground mt-3">
        {translate("resources.contract_types.hint.sort_order")}
      </p>
    </List>
  );
}

/**
 * Mobile contract types list: title and search in the fixed header, the "new
 * contract type" button below it and one tappable row per type (name, order
 * and an "inactive" badge). Tapping a row opens the edit form.
 */
export const ContractTypeListMobile = () => {
  const translate = useTranslate();

  return (
    <ListBase<ContractType>
      perPage={100}
      sort={{ field: "sort_order", order: "ASC" }}
      queryOptions={{
        onError: () => {
          /* the content below renders the error itself */
        },
      }}
    >
      <MobileListHeader
        title={translate("resources.contract_types.name", { smart_count: 2 })}
        searchPlaceholder={translate("resources.contract_types.filters.search")}
      />
      <MobileContent search>
        <CanAccess resource="contract_types" action="create">
          <Button asChild className="mb-4 h-11 w-full text-base">
            <Link to="/contract_types/create">
              <Plus className="size-5" />
              {translate("resources.contract_types.action.new")}
            </Link>
          </Button>
        </CanAccess>
        <ContractTypeListContentMobile />
        <p className="text-xs text-muted-foreground mt-4">
          {translate("resources.contract_types.hint.sort_order")}
        </p>
      </MobileContent>
    </ListBase>
  );
};

const ContractTypeListContentMobile = () => {
  const translate = useTranslate();
  const { data, error, isPending, refetch } = useListContext<ContractType>();
  const redirect = useRedirect();
  const { canAccess: canEdit } = useCanAccess({
    resource: "contract_types",
    action: "edit",
  });

  if (isPending) {
    return (
      <div className="divide-y rounded-lg border bg-card">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="px-4 py-3">
            <Skeleton className="h-5 w-40" />
          </div>
        ))}
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4 text-center">
        <p className="mb-4 text-muted-foreground">
          {translate("resources.contract_types.list.error_loading")}
        </p>
        <Button onClick={() => refetch()}>
          <RotateCcw />
          {translate("crm.common.retry")}
        </Button>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div
        className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground"
        role="status"
      >
        {translate("resources.contract_types.list.no_results")}
      </div>
    );
  }

  return (
    <ItemGroup className="divide-y overflow-hidden rounded-lg border bg-card">
      {data.map((record) => (
        <Item
          key={record.id}
          size="sm"
          className={canEdit ? "cursor-pointer rounded-none" : "rounded-none"}
          onClick={
            canEdit
              ? () => redirect("edit", "contract_types", record.id)
              : undefined
          }
        >
          <ItemContent className="min-w-0">
            <ItemTitle className="text-base">{record.name}</ItemTitle>
            {record.active ? null : (
              <div className="mt-1">
                <Badge variant="outline" className="text-muted-foreground">
                  {translate("resources.contract_types.inactive")}
                </Badge>
              </div>
            )}
          </ItemContent>
          <ItemActions>
            <span className="text-sm text-muted-foreground tabular-nums">
              {record.sort_order}
            </span>
          </ItemActions>
        </Item>
      ))}
    </ItemGroup>
  );
};
